import {
  BriefcaseBusiness,
  ExternalLink,
  Settings,
  ShieldUser,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { IgTooltip } from "@intelligems/ig-theme/IgTooltip";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { IconButton, List, useTheme } from "@mui/material";
import Stack from "@mui/material/Stack";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { IgListItemLink } from "@src/components/Navigation/BaseSideNav/IgListItemLink";
import { SettingsRoutes } from "@src/components/Utils/routes";
import { useEntityLabel } from "@src/hooks/useEntityLabel";
import { useShowBillingPanel } from "@src/hooks/useShowBillingPanel";
import { useShowBrandManagement } from "@src/hooks/useShowBrandManagement";
import { useShowSlackSettings } from "@src/hooks/useShowSlackSettings";
import { getAdminOrgPath, SETTINGS_ROUTES } from "@src/routes/routes";
import { SettingsSectionId } from "@src/types/navigation";

const settingsSections = [
  { route: SettingsRoutes.general, sectionId: "general" },
  { route: SettingsRoutes.cogs, sectionId: "cogs" },
  { route: SettingsRoutes.currency, sectionId: "currency" },
  { route: SettingsRoutes.analytics, sectionId: "analytics" },
  { route: SettingsRoutes.webhooks, sectionId: "webhooks" },
  { route: SettingsRoutes.customShipping, sectionId: "custom-shipping" },
  { route: SettingsRoutes.billing, sectionId: "billing" },
];

const adminSections = [
  { route: SettingsRoutes.manageFunctions, sectionId: "manage-functions" },
  {
    route: SettingsRoutes.pluginConfigurations,
    sectionId: "plugin-configurations",
  },
  {
    route: SettingsRoutes.shopifyConfigurations,
    sectionId: "shopify-configurations",
  },
  { route: SettingsRoutes.devTools, sectionId: "dev-tools" },
  { route: SettingsRoutes.permissions, sectionId: "permissions" },
  {
    route: SettingsRoutes.userPermissions,
    sectionId: "user-permissions",
  },
  { route: SettingsRoutes.atlasThemeAccess, sectionId: "atlas-theme-access" },
];

const LEFT_PAD = "32px"; // equal to icon width

export const SettingsSideNavBarBody = () => {
  const theme = useTheme();
  const [currentHash, setCurrentHash] = useState(window.location.hash);
  const isAdmin = useIsAdmin();
  const entityLabel = useEntityLabel();
  const showBrandManagementSection = useShowBrandManagement();
  const showSlackSettings = useShowSlackSettings();
  const showBillingPanel = useShowBillingPanel();
  const organizationId = useGetOrg().data.org.id;
  const customShippingEnabled =
    useConfiguration<boolean>("custom_shipping_providers")?.value ?? false;

  const agencySections = [
    {
      route: SettingsRoutes.agencyBilling,
      sectionId: SettingsSectionId.AgencyBilling,
    },
    {
      route: SettingsRoutes.slackSettings,
      sectionId: SettingsSectionId.SlackSettings,
    },
  ];

  const agencySectionsToShow = agencySections.filter(({ sectionId }) => {
    if (
      sectionId === SettingsSectionId.AgencyBilling &&
      !showBrandManagementSection
    ) {
      return false;
    }
    if (sectionId === SettingsSectionId.SlackSettings && !showSlackSettings) {
      return false;
    }
    return true;
  });

  // Listen for hash changes (including from replaceState + dispatchEvent)
  useEffect(() => {
    const handleHashChange = () => {
      setCurrentHash(window.location.hash);
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const handleSectionClick = (sectionId: string) => (e?: React.MouseEvent) => {
    e?.preventDefault();

    // Update URL hash and state
    window.history.replaceState(
      null,
      "",
      `${SETTINGS_ROUTES.Settings}#${sectionId}`
    );
    setCurrentHash(`#${sectionId}`);

    // Scroll to section
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <List sx={{ px: 2, paddingTop: 4 }}>
      <Stack spacing={8}>
        <Stack spacing={4}>
          <Stack
            direction="row"
            spacing={2}
            sx={{ alignItems: "center", pl: 2 }}
          >
            <Settings
              size={16}
              style={{ color: theme.igIconColors.lightHover }}
            />
            <IgTypography variant="h3" fontWeight={600}>
              Settings
            </IgTypography>
          </Stack>

          <Stack>
            {settingsSections
              .filter(
                ({ sectionId }) => sectionId !== "billing" || showBillingPanel
              )
              .filter(
                ({ sectionId }) =>
                  sectionId !== "custom-shipping" || customShippingEnabled
              )
              .map(({ route, sectionId }) => (
                <IgListItemLink
                  key={sectionId}
                  Route={route}
                  drawerOpen={true}
                  hasAccess={true}
                  exact={true}
                  isLink={false}
                  handleClick={handleSectionClick(sectionId)}
                  sx={{ paddingLeft: LEFT_PAD }}
                  currentHash={currentHash}
                />
              ))}
          </Stack>
        </Stack>
        {agencySectionsToShow.length ? (
          <Stack spacing={4}>
            <Stack
              direction="row"
              spacing={2}
              sx={{ alignItems: "center", pl: 2 }}
            >
              <BriefcaseBusiness
                size={16}
                style={{ color: theme.igIconColors.lightHover }}
              />
              <IgTypography variant="h3" fontWeight={600}>
                {entityLabel}
              </IgTypography>
            </Stack>
            <Stack>
              {agencySectionsToShow.map(({ route, sectionId }) => {
                if (route) {
                  return (
                    <IgListItemLink
                      key={sectionId}
                      Route={route}
                      drawerOpen={true}
                      hasAccess={true}
                      exact={true}
                      isLink={false}
                      handleClick={handleSectionClick(sectionId)}
                      sx={{ paddingLeft: LEFT_PAD }}
                      currentHash={currentHash}
                    />
                  );
                }
                return null;
              })}
            </Stack>
          </Stack>
        ) : null}

        {isAdmin && (
          <Stack spacing={4}>
            <Stack
              direction="row"
              spacing={2}
              sx={{ alignItems: "center", pl: 2 }}
            >
              <ShieldUser
                size={16}
                style={{ color: theme.igIconColors.lightHover }}
              />
              <IgTypography variant="h3" fontWeight={600}>
                Admin
              </IgTypography>
              <IgTooltip title="Open admin organization page">
                <IconButton
                  component={RouterLink}
                  to={getAdminOrgPath(organizationId)}
                  size="small"
                  aria-label="Open admin organization page"
                >
                  <ExternalLink
                    size={16}
                    style={{ color: theme.igIconColors.lightHover }}
                  />
                </IconButton>
              </IgTooltip>
            </Stack>

            <Stack>
              {adminSections.map(({ route, sectionId }) => (
                <IgListItemLink
                  key={sectionId}
                  Route={route}
                  drawerOpen={true}
                  hasAccess={true}
                  exact={true}
                  isLink={false}
                  handleClick={handleSectionClick(sectionId)}
                  sx={{ paddingLeft: LEFT_PAD }}
                  currentHash={currentHash}
                />
              ))}
            </Stack>
          </Stack>
        )}
      </Stack>
    </List>
  );
};
